/* eslint-disable no-unused-expressions */
import React, { PureComponent } from 'react';
import { Column } from '@nextiva/crm-datatable';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';

import { pathFrom } from 'utils/router-helpers';
import { isLoading, hasError } from 'utils/meta-selectors';
import { AsyncView, withRouterQuery, AdminTable } from 'components';

import { getResources, getSettings, getActions } from '../duck/actions';
import {
  getActionsGroupedByActionType,
  getActionsForFields,
  getScopeFromSettings,
  getApplicationResourceFields,
  getApplicationCurrentResource,
  FIELDS_DEFAULT_QUERY,
} from '../duck/selectors';
import { getCurrentProductEndpoints } from '../../RoleProducts/duck/selectors';
import { createDisplayPreferences as createModelDisplayPreferences } from '../ModelsTable';
import { permissionSelectRenderer, modelNameRenderer } from '../tableRenderers';

import styles from './FieldsPermissions.scss';

const defaultOption = {
  value: 0,
  label: 'Default',
  tooltip:
    'Ensures that the field-level permission right does not affect a particular setting.',
};

const createDisplayPreferences = (configs) => [
  {
    name: 'name',
    label: 'fieldName',
    display: true,
    type: 'text',
    width: '30%',
    shouldRenderFilter: true,
    shouldRenderSort: true,
    typeConfig: {
      defaultOperator: 'contains',
      operatorOptions: [
        {
          value: 'contains',
          label: 'Field Name contains',
        },
      ],
    },
  },
  {
    name: 'permission',
    label: 'permission',
    display: true,
    width: '70%',
    cellRenderer: permissionSelectRenderer({ ...configs, defaultOption }),
  },
];

class FieldsPermissions extends PureComponent {
  static defaultProps = {
    defaultQuery: FIELDS_DEFAULT_QUERY,
  };

  constructor(props) {
    super(props);
    const { hash } = window.location;
    const [, , actionType, roleId, appSlug, modelName] = hash.split('/');

    this.actionType = actionType;
    this.roleId = roleId.replace('%20', ' ');
    this.appSlug = appSlug;
    this.modelName = modelName;
  }

  componentDidMount() {
    const {
      getActions,
      getResources,
      getSettings,
      endpoints,
      productScope,
      options,
      productResource,
    } = this.props;

    if (endpoints) {
      !productScope && endpoints.settings && getSettings(endpoints.settings);
      !options.length && endpoints.actions && getActions(endpoints.actions);
      !productResource.columns &&
        endpoints.resources &&
        getResources(endpoints.resources);
    }
  }

  renderHeaderSelectors() {
    const { productResource, productScope, resourceOptions } = this.props;

    const ModelPermission = createModelDisplayPreferences(
      true,
      true,
      resourceOptions,
      styles.modelSelectorComponent,
      productScope,
    )[1];

    return ModelPermission.cellRenderer({ rowData: productResource });
  }

  renderFieldsPermissionsTable() {
    const {
      productResource,
      productScope,
      options,
      handleFiltersChange,
      handleSortChange,
      orderFromParams,
      filtersFromParams,
      getTableQuery,
      defaultQuery,
      fields,
    } = this.props;
    const { filters, sort } = getTableQuery();
    const { sort: defaultSort } = defaultQuery;
    const displayPrefrencesConfigs = {
      options,
      className: styles.fieldsSelectorComponent,
      productScope,
    };
    const fieldsDisplayPreferences = createDisplayPreferences(
      displayPrefrencesConfigs,
    );
    const modelNameComponent = modelNameRenderer({ isOpen: true })({
      cellData: productResource.name,
      rowData: { columns: fields },
    });

    const filterDecoders = fieldsDisplayPreferences.reduce(
      (res, { name, decodeFilter }) => ({ ...res, [name]: decodeFilter }),
      {},
    );

    return (
      <div className={styles.fieldTableContainer}>
        <AdminTable
          isLoading={false}
          data={fields}
          displayPreferences={fieldsDisplayPreferences}
          disableGear
          disableCheckboxes
          onFiltersChange={(data) => handleFiltersChange(filterDecoders)(data)}
          onSortChange={handleSortChange}
        >
          {fieldsDisplayPreferences.map((item, index) => (
            <Column
              key={index}
              name={item.name}
              type={item.type}
              cellRenderer={item.cellRenderer}
              typeConfig={item.typeConfig}
              shouldRenderSort={get(item, 'shouldRenderSort', false)}
              shouldRenderFilter={get(item, 'shouldRenderFilter', false)}
              getCellData={(rowData, name) =>
                get(rowData, item.valueName || name)
              }
              order={orderFromParams(sort || defaultSort, item.name)}
              filterData={filtersFromParams(
                filters,
                item.name,
                item.encodeFilter,
              )}
            >
              {item.label === 'fieldName' && modelNameComponent}
              {item.label === 'permission' && this.renderHeaderSelectors()}
            </Column>
          ))}
        </AdminTable>
      </div>
    );
  }

  render() {
    const {
      isLoading,
      hasError,
      // params: { appSlug, modelName, roleId, actionType },
    } = this.props;
    const servicePath = pathFrom(
      'roles',
      this.actionType,
      this.roleId,
      'permissions',
      this.appSlug,
    );

    return (
      <div className={styles.page}>
        <BreadcrumbsItem
          to={servicePath}
          data-testid="PlatformAdminUI-FieldsPermissions-BreadcrumbsItem"
        >
          Service {this.appSlug} Permissions
        </BreadcrumbsItem>
        <BreadcrumbsItem
          to={pathFrom(servicePath, this.modelName)}
          data-testid="PlatformAdminUI-FieldsPermissions-FormPermission"
        >
          {this.modelName} Form Permissions
        </BreadcrumbsItem>
        <AsyncView
          loading={isLoading}
          error={hasError && 'Could not load data for form permissions!'}
        >
          {this.renderFieldsPermissionsTable()}
        </AsyncView>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { hash } = window.location;
  const [, , , , appSlug, modelName] = hash.split('/');

  return {
    endpoints: getCurrentProductEndpoints(state, appSlug),
    productResource: getApplicationCurrentResource(state, modelName),
    fields: getApplicationResourceFields(state, modelName),
    isLoading: isLoading(state, [
      'productResources',
      'productSettings',
      'productActions',
    ]),
    hasError: hasError(state, [
      'productResources',
      'productSettings',
      'productActions',
    ]),
    resourceOptions: getActionsGroupedByActionType(state),
    options: getActionsForFields(state),
    productScope: getScopeFromSettings(state),
  };
};

export default connect(mapStateToProps, {
  getResources,
  getSettings,
  getActions,
})(withRouterQuery(FieldsPermissions));
